import type {
  DirectorScenePayload,
  Id,
  ArchiveSceneMode,
} from "@supermanus/document-model";

export type DirectorForkRow = {
  id: Id;
  projectId: Id;
  userId: Id;
  episodeId: Id;
  sourceSceneId: Id;
  payload: DirectorScenePayload;
  updatedAt: string;
};

export type DirectorArchiveRow = {
  id: Id;
  projectId: Id;
  userId: Id;
  episodeId: Id;
  sourceSceneId: Id;
  archiveLabel?: string | null;
  archivedAt: string;
  payload: DirectorScenePayload;
};

async function apiJson<T>(
  apiBase: string,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${apiBase.replace(/\/$/, "")}${path}`, {
    credentials: "include",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = { error: text };
    }
  }
  if (!res.ok) {
    const payload = data as { error?: string; message?: string } | null;
    throw new Error(payload?.error || payload?.message || `HTTP ${res.status}`);
  }
  return data as T;
}

export async function listDirectorForks(
  apiBase: string,
  projectId: Id,
  episodeId: Id,
): Promise<DirectorForkRow[]> {
  const data = await apiJson<{ forks: DirectorForkRow[] }>(
    apiBase,
    `/projects/${encodeURIComponent(projectId)}/director-forks?episodeId=${encodeURIComponent(episodeId)}`,
  );
  return data.forks ?? [];
}

export async function upsertDirectorFork(
  apiBase: string,
  projectId: Id,
  sourceSceneId: Id,
  episodeId: Id,
  payload: DirectorScenePayload,
): Promise<DirectorForkRow> {
  return apiJson(
    apiBase,
    `/projects/${encodeURIComponent(projectId)}/director-forks/${encodeURIComponent(sourceSceneId)}`,
    {
      method: "PUT",
      body: JSON.stringify({ episodeId, payload }),
    },
  );
}

export async function deleteDirectorFork(
  apiBase: string,
  projectId: Id,
  sourceSceneId: Id,
): Promise<void> {
  await apiJson(
    apiBase,
    `/projects/${encodeURIComponent(projectId)}/director-forks/${encodeURIComponent(sourceSceneId)}`,
    { method: "DELETE" },
  );
}

export async function listDirectorArchives(
  apiBase: string,
  projectId: Id,
  sourceSceneId: Id,
): Promise<DirectorArchiveRow[]> {
  const data = await apiJson<{ archives: DirectorArchiveRow[] }>(
    apiBase,
    `/projects/${encodeURIComponent(projectId)}/director-archives/${encodeURIComponent(sourceSceneId)}`,
  );
  return data.archives ?? [];
}

export async function createDirectorArchive(
  apiBase: string,
  projectId: Id,
  sourceSceneId: Id,
  options: {
    episodeId: Id;
    mode: ArchiveSceneMode;
    replaceArchiveId?: Id;
    label?: string;
    payload: DirectorScenePayload;
  },
): Promise<DirectorArchiveRow> {
  return apiJson(
    apiBase,
    `/projects/${encodeURIComponent(projectId)}/director-archives/${encodeURIComponent(sourceSceneId)}`,
    {
      method: "POST",
      body: JSON.stringify({
        episodeId: options.episodeId,
        mode: options.mode,
        replaceArchiveId: options.replaceArchiveId,
        label: options.label,
        payload: options.payload,
      }),
    },
  );
}

export async function deleteDirectorArchive(
  apiBase: string,
  projectId: Id,
  archiveId: Id,
): Promise<void> {
  await apiJson(
    apiBase,
    `/projects/${encodeURIComponent(projectId)}/director-archives/by-id/${encodeURIComponent(archiveId)}`,
    { method: "DELETE" },
  );
}
